import { useCallback, useMemo, useState } from "react";
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import type {
  LegacyCaseCollision,
  LegacyDocumentSummary,
} from "../../../types/legacyCases";
import { LegacyCaseRequestError } from "../../../services/legacyCases.service";
import LegacyExistingCaseDocumentAttachment from "./LegacyExistingCaseDocumentAttachment";
import { LEGACY_CASE_MODAL_Z_INDEX } from "./legacyCaseOverlay";

interface Props {
  collision?: LegacyCaseCollision;
  documents: LegacyDocumentSummary[];
  error?: Error | null;
  loading: boolean;
  onRefresh: () => void;
}

export default function LegacyCaseCollisionNotice({
  collision,
  documents,
  error,
  loading,
  onRefresh,
}: Props) {
  const [dismissedCase, setDismissedCase] = useState("");
  const existing = collision?.importBlocked
    ? collision.existingCase
    : undefined;
  const opened = useMemo(
    () => Boolean(existing && existing.id !== dismissedCase),
    [dismissedCase, existing]
  );
  const errorMessage = useMemo(
    () =>
      error instanceof LegacyCaseRequestError && error.status === 403
        ? "Your account cannot check active cases. The archive and saved review are still available."
        : "The existing-case check could not finish. Retry the check; your archive and saved review are still available.",
    [error]
  );
  const handleClose = useCallback(
    () => setDismissedCase(existing?.id || ""),
    [existing?.id]
  );
  return (
    <Stack gap="sm">
      {loading && !collision && (
        <Text size="sm" role="status">
          Checking whether this case number is already in the LIS…
        </Text>
      )}
      {error && (
        <Alert color="orange" title="Existing-case check unavailable">
          <Stack gap="sm">
            <Text size="sm">{errorMessage}</Text>
            <Button variant="light" onClick={onRefresh} loading={loading}>
              Retry case check
            </Button>
          </Stack>
        </Alert>
      )}
      {collision && existing && (
        <>
          <Alert color="orange" title="This case number is already in the LIS">
            <Stack gap="sm">
              <Text size="sm">
                {existing.caseNumber} already exists
                {existing.status ? ` (${existing.status})` : ""}. It may have
                been accessioned again. A second case cannot be imported with
                this number. Your archive draft and original documents remain
                available.
              </Text>
              <Group>
                <Button
                  component={Link}
                  to={`/cases/${existing.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="light"
                >
                  Open existing case
                </Button>
                <Button variant="subtle" onClick={onRefresh} loading={loading}>
                  Refresh case check
                </Button>
              </Group>
              <LegacyExistingCaseDocumentAttachment
                collision={collision}
                documents={documents}
                disabled={Boolean(error)}
              />
            </Stack>
          </Alert>
          <Modal
            zIndex={LEGACY_CASE_MODAL_Z_INDEX}
            opened={opened}
            onClose={handleClose}
            title="Case number already exists"
            centered
            closeOnClickOutside={false}
          >
            <Stack gap="md">
              <Alert color="orange">
                {existing.caseNumber} is already an active LIS case. Review it
                before continuing; this archive cannot be imported as another
                case with the same number.
              </Alert>
              <Text size="sm">
                Your review work and original files are retained. You can keep
                reviewing the archive and, when permitted, attach an archived
                document to the existing case.
              </Text>
              <Group justify="space-between">
                <Button variant="default" onClick={handleClose} data-autofocus>
                  Keep reviewing archive
                </Button>
                <Button
                  component={Link}
                  to={`/cases/${existing.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open existing case
                </Button>
              </Group>
            </Stack>
          </Modal>
        </>
      )}
    </Stack>
  );
}
